import "@/App.css";
import "@/index.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { TaskDialogProvider } from "@/contexts/TaskDialogContext";
import { CustomizeProvider } from "@/contexts/CustomizeContext";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import PortalRoute from "@/components/layout/PortalRoute";
import TopupOverlay from "@/components/TopupOverlay";

// Route-level code splitting — every page is a separate chunk so the
// initial bundle (Login + Dashboard frame) downloads in a few hundred
// KB instead of ~2 MB. Pages we own that are eagerly used on login are
// still cheap because React/Webpack will start prefetching them in the
// background as soon as the auth resolves.
//
// IMPORTANT: keep Login eager — it's the first paint for unauthenticated
// users and we don't want a Suspense flash on the landing page.
import Login from "@/pages/Login";

// webpackPrefetch hints — these chunks are nearly always the first
// destination after auth resolves (Dashboard for staff, Portal for
// clients). The browser fetches them with low priority while idle so
// the first nav after login feels instant.
const Dashboard           = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Dashboard"));
const Customize           = lazy(() => import("@/pages/Customize"));
const Leads               = lazy(() => import("@/pages/Leads"));
const Clients             = lazy(() => import("@/pages/Clients"));
const ClientProfile       = lazy(() => import("@/pages/ClientProfile"));
const Tasks               = lazy(() => import("@/pages/Tasks"));
const Approvals           = lazy(() => import("@/pages/Approvals"));
const ServicesPackages    = lazy(() => import("@/pages/ServicesPackages"));
const Finance             = lazy(() => import("@/pages/Finance"));
const Employees           = lazy(() => import("@/pages/Employees"));
const HR                  = lazy(() => import("@/pages/HR"));
const TaskTemplates       = lazy(() => import("@/pages/TaskTemplates"));
const Notifications       = lazy(() => import("@/pages/Notifications"));
const Settings            = lazy(() => import("@/pages/Settings"));
const Schedule            = lazy(() => import("@/pages/Schedule"));
const AutomatedCalendar   = lazy(() => import("@/pages/AutomatedCalendar"));
const Cases               = lazy(() => import("@/pages/Cases"));
const Roles               = lazy(() => import("@/pages/Roles"));
const Reports             = lazy(() => import("@/pages/Reports"));
const Calendar            = lazy(() => import("@/pages/Calendar"));
const Meetings            = lazy(() => import("@/pages/Meetings"));
const AccountManagement   = lazy(() => import("@/pages/AccountManagement"));
const Shooting            = lazy(() => import("@/pages/Shooting"));
const Trash               = lazy(() => import("@/pages/Trash"));
const Quotations          = lazy(() => import("@/pages/Quotations"));
const PaidAds             = lazy(() => import("@/pages/PaidAds"));
const VideoEditing        = lazy(() => import("@/pages/VideoEditing"));
const CreativeSchedule    = lazy(() => import("@/pages/CreativeSchedule"));
const Chat                = lazy(() => import("@/pages/Chat"));
const Trends              = lazy(() => import("@/pages/Trends"));
const Permissions         = lazy(() => import("@/pages/Permissions"));
const Transfers           = lazy(() => import("@/pages/Transfers"));
const TransferDetail      = lazy(() => import("@/pages/TransferDetail"));
const PublicTransfer      = lazy(() => import("@/pages/PublicTransfer"));
const BulkWhatsapp        = lazy(() => import("@/pages/BulkWhatsapp"));
const SalesHunter         = lazy(() => import("@/pages/SalesHunter"));
const AdminDashboard      = lazy(() => import("@/pages/AdminDashboard"));
const ManagerDailyTasks   = lazy(() => import("@/pages/ManagerDailyTasks"));
const DesignerGrid        = lazy(() => import("@/pages/DesignerGrid"));
const AuditLog            = lazy(() => import("@/pages/AuditLog"));
const BulkPost            = lazy(() => import("@/pages/BulkPost"));
const Drive               = lazy(() => import("@/pages/Drive"));
const DriveHealth         = lazy(() => import("@/pages/DriveHealth"));
const MonthlyReport       = lazy(() => import("@/pages/MonthlyReport"));
const PdfToLink           = lazy(() => import("@/pages/PdfToLink"));
const AITools             = lazy(() => import("@/pages/AITools"));
const AIWebsiteBuilder    = lazy(() => import("@/pages/AIWebsiteBuilder"));
const DailyCheckin        = lazy(() => import("@/pages/DailyCheckin"));
const PerformancePage     = lazy(() => import("@/pages/PerformancePage"));
const ManagerTodos        = lazy(() => import("@/pages/ManagerTodos"));
const Supervision         = lazy(() => import("@/pages/Supervision"));
const ManagementCalendar  = lazy(() => import("@/pages/ManagementCalendar"));
const OperationCalendar   = lazy(() => import("@/pages/OperationCalendar"));
const ContentPlans        = lazy(() => import("@/pages/ContentPlans"));
const WorkTracker         = lazy(() => import("@/pages/WorkTracker"));
const Retention           = lazy(() => import("@/pages/Retention"));
const Loyalty             = lazy(() => import("@/pages/Loyalty"));
const DeptReports         = lazy(() => import("@/pages/DeptReports"));
const PortalDashboard     = lazy(() => import(/* webpackPrefetch: true */ "@/pages/portal/PortalDashboard"));
const PortalFiles         = lazy(() => import("@/pages/portal/PortalFiles"));
const PortalCalendar      = lazy(() => import("@/pages/portal/PortalCalendar"));
const PortalInvoices      = lazy(() => import("@/pages/portal/PortalInvoices"));
const PortalAds           = lazy(() => import("@/pages/portal/PortalAds"));
const PortalReceipts      = lazy(() => import("@/pages/portal/PortalReceipts"));
const PortalFeedback      = lazy(() => import("@/pages/portal/PortalFeedback"));


// Lightweight loading shell shown while a route chunk downloads.
// Matches the app's teal brand and stays calm so it never flashes
// noisy spinners.
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="route-loading">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 rounded-full border-2 border-teal/30 border-t-teal animate-spin" />
        <div className="text-xs text-muted-foreground tracking-wider">Loading…</div>
      </div>
    </div>
  );
}


// Redirect role=client users from / to /portal
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role === "client") return <Navigate to="/portal" replace />;
  return <ProtectedRoute><Dashboard /></ProtectedRoute>;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CustomizeProvider>
        <TaskDialogProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<RootRedirect />} />
              <Route path="/supervision" element={<ProtectedRoute><Supervision /></ProtectedRoute>} />
              <Route path="/management-calendar" element={<ProtectedRoute><ManagementCalendar /></ProtectedRoute>} />
              <Route path="/operation-calendar" element={<ProtectedRoute><OperationCalendar /></ProtectedRoute>} />
              <Route path="/work-tracker" element={<ProtectedRoute><WorkTracker /></ProtectedRoute>} />
              <Route path="/retention" element={<ProtectedRoute><Retention /></ProtectedRoute>} />
              <Route path="/loyalty" element={<ProtectedRoute><Loyalty /></ProtectedRoute>} />
              <Route path="/dept-reports" element={<ProtectedRoute><DeptReports /></ProtectedRoute>} />
              <Route path="/content-plans" element={<ProtectedRoute><ContentPlans /></ProtectedRoute>} />
              <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
              <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
              <Route path="/clients/:id" element={<ProtectedRoute><ClientProfile /></ProtectedRoute>} />
              <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
              <Route path="/requests" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
              <Route path="/cases" element={<ProtectedRoute><Cases /></ProtectedRoute>} />
              <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
              <Route path="/automated-calendar" element={<ProtectedRoute><AutomatedCalendar /></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
              <Route path="/meetings" element={<ProtectedRoute><Meetings /></ProtectedRoute>} />
              <Route path="/account-management" element={<ProtectedRoute><AccountManagement /></ProtectedRoute>} />
              <Route path="/shooting" element={<ProtectedRoute><Shooting /></ProtectedRoute>} />
              <Route path="/trash" element={<ProtectedRoute><Trash /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/customize" element={<ProtectedRoute><Customize /></ProtectedRoute>} />
              <Route path="/approvals" element={<ProtectedRoute><Approvals /></ProtectedRoute>} />
              <Route path="/services" element={<ProtectedRoute><ServicesPackages /></ProtectedRoute>} />
              <Route path="/finance" element={<ProtectedRoute><Finance /></ProtectedRoute>} />
              <Route path="/quotations" element={<ProtectedRoute><Quotations /></ProtectedRoute>} />
              <Route path="/paid-ads" element={<ProtectedRoute><PaidAds /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
              <Route path="/trends" element={<ProtectedRoute><Trends /></ProtectedRoute>} />
              <Route path="/permissions" element={<ProtectedRoute><Permissions /></ProtectedRoute>} />
              <Route path="/video-editing" element={<ProtectedRoute><VideoEditing /></ProtectedRoute>} />
              <Route path="/graphic-design" element={<ProtectedRoute><CreativeSchedule kind="graphic_design" /></ProtectedRoute>} />
              <Route path="/content-creator" element={<ProtectedRoute><CreativeSchedule kind="content_creator" /></ProtectedRoute>} />
              <Route path="/paid-ads-schedule" element={<ProtectedRoute><CreativeSchedule kind="paid_ads" /></ProtectedRoute>} />
              <Route path="/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
              <Route path="/hr" element={<ProtectedRoute><HR /></ProtectedRoute>} />
              <Route path="/task-templates" element={<ProtectedRoute><TaskTemplates /></ProtectedRoute>} />
              <Route path="/roles" element={<ProtectedRoute><Roles /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/transfers" element={<ProtectedRoute><Transfers /></ProtectedRoute>} />
              <Route path="/transfers/:id" element={<ProtectedRoute><TransferDetail /></ProtectedRoute>} />
              <Route path="/bulk-whatsapp" element={<ProtectedRoute><BulkWhatsapp /></ProtectedRoute>} />
              <Route path="/bulk-post" element={<ProtectedRoute><BulkPost /></ProtectedRoute>} />
              <Route path="/drive" element={<ProtectedRoute><Drive /></ProtectedRoute>} />
              <Route path="/drive-health" element={<ProtectedRoute><DriveHealth /></ProtectedRoute>} />
              <Route path="/monthly-report" element={<ProtectedRoute><MonthlyReport /></ProtectedRoute>} />
              <Route path="/pdf-to-link" element={<ProtectedRoute><PdfToLink /></ProtectedRoute>} />
              <Route path="/ai-tools" element={<ProtectedRoute><AITools /></ProtectedRoute>} />
              <Route path="/ai-website-builder" element={<ProtectedRoute><AIWebsiteBuilder /></ProtectedRoute>} />
              <Route path="/daily-checkin" element={<ProtectedRoute><DailyCheckin /></ProtectedRoute>} />
              <Route path="/performance" element={<ProtectedRoute><PerformancePage /></ProtectedRoute>} />
              <Route path="/manager-todos" element={<ProtectedRoute><ManagerTodos /></ProtectedRoute>} />
              <Route path="/sales-hunter" element={<ProtectedRoute><SalesHunter /></ProtectedRoute>} />
              <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/manager/daily-tasks" element={<ProtectedRoute><ManagerDailyTasks /></ProtectedRoute>} />
              <Route path="/designer-grid" element={<ProtectedRoute><DesignerGrid /></ProtectedRoute>} />
              <Route path="/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />

              {/* PUBLIC — no auth wrapper. Recipients open this with the share link. */}
              <Route path="/t/:token" element={<PublicTransfer />} />

              {/* Client Portal */}
              <Route path="/portal" element={<PortalRoute><PortalDashboard /></PortalRoute>} />
              <Route path="/portal/files" element={<PortalRoute><PortalFiles /></PortalRoute>} />
              <Route path="/portal/calendar" element={<PortalRoute><PortalCalendar /></PortalRoute>} />
              <Route path="/portal/invoices" element={<PortalRoute><PortalInvoices /></PortalRoute>} />
              <Route path="/portal/ads" element={<PortalRoute><PortalAds /></PortalRoute>} />
              <Route path="/portal/receipts" element={<PortalRoute><PortalReceipts /></PortalRoute>} />
              <Route path="/portal/feedback" element={<PortalRoute><PortalFeedback /></PortalRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <TopupOverlay />
        </BrowserRouter>
        </TaskDialogProvider>
        </CustomizeProvider>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
